import React, { useEffect, useRef, useState, useCallback } from "react";
import { marked } from "marked";

const ChatApp = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const msgMap = useRef(new Map());

  const renderMessages = useCallback((responseText) => {
    const lines = responseText.split("\n");
    const newMessages = lines
      .filter((line) => line.length > 1)
      .map((line) => JSON.parse(line));

    newMessages.forEach((msg) => {
      msgMap.current.set(msg.timestamp, msg);
    });

    setMessages(Array.from(msgMap.current.values()));
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    }, 100);
  }, []);

  const onFetchResponse = useCallback(
    async (response) => {
      let text = "";
      const decoder = new TextDecoder();

      if (response.ok && response.body) {
        const reader = response.body.getReader();
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          text += decoder.decode(value);
          renderMessages(text);
        }
        renderMessages(text);
        setLoading(false);
      } else {
        const errorText = await response.text();
        console.error(`Unexpected response: ${response.status}`, {
          response,
          errorText,
        });
        throw new Error(`Unexpected response: ${response.status}`);
      }
    },
    [renderMessages],
  );

  const onError = useCallback((error) => {
    console.error(error);
    setLoading(false);
    document.getElementById("error")?.classList.remove("d-none");
  }, []);

  useEffect(() => {
    fetch("/chat/").then(onFetchResponse).catch(onError);
  }, [onFetchResponse, onError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setInput("");

    try {
      const formData = new FormData();
      formData.append("prompt", input);

      const response = await fetch(
        `${process.env.REACT_APP_AI_BASE_URL}/conversation-chat/`,
        {
          method: "POST",
          body: formData,
        },
      );

      await onFetchResponse(response);
    } catch (err) {
      onError(err);
    }
  };

  return (
    <div className="chat-container">
      <div id="conversation">
        {messages.map((msg) => (
          <div
            key={msg.timestamp}
            id={`msg-${msg.timestamp}`}
            title={`${msg.role} at ${msg.timestamp}`}
            className={`border-top pt-2 ${msg.role}`}
            dangerouslySetInnerHTML={{ __html: marked.parse(msg.content) }}
          />
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        <input
          id="prompt-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={loading}
          placeholder="Type your prompt here..."
        />
        <button type="submit" disabled={loading}>
          Send
        </button>
      </form>

      {loading && (
        <div id="spinner" className="active">
          Loading...
        </div>
      )}
      <div id="error" className="d-none">
        Something went wrong!
      </div>
    </div>
  );
};

export default ChatApp;
